import { z } from 'zod';
import { FlowRoleSchema } from '../flow/role';

/**
 * `GET /api/me` wire contract. One schema, used by the server (`src/api/me.ts`
 * parses its payload through it) and the web (`UserControlsContext`).
 *
 * Two tiers of strictness:
 *  - SECURITY fields (`isAdmin`, `canImpersonate`, `isManager`,
 *    `isMarketingUser`, `impersonation`, `userId`) are strict. A response that
 *    gets any of them wrong is rejected, never defaulted: defaulting would
 *    change who the app believes you are (admin gates off, impersonation
 *    banner hidden while the server still impersonates).
 *  - Everything else is tolerant so a harmless drift (a new `flowRole` member
 *    from a newer server) does not take the whole bootstrap down.
 */
const ImpersonationMetaSchema = z.object({
  active: z.boolean(),
  canUnlockWrites: z.boolean(),
  actingAs: z.string().nullable(),
  actingAsSlackId: z.string().nullable(),
  impersonatorId: z.number(),
  impersonatorEmail: z.string().nullable(),
  /** 'demo' for a seeded demo identity (acting.kind === 'demo'), 'colleague'
   * otherwise. Optional so an /api/me response from a server that predates
   * the field still parses. */
  target: z.enum(['colleague', 'demo']).optional(),
});

/**
 * A Flow team or null, for both the lensed and the real field. The outer
 * `z.string().nullable()` is what makes a MISSING key fail: a bare
 * `.catch(null)` would swallow that error too and quietly read as "no team".
 * The inner `.catch(null)` degrades a team this bundle does not know to null.
 */
const FlowRoleOrNull = z.string().nullable().pipe(FlowRoleSchema.nullable().catch(null));

export const UserControlsSchema = z.object({
  isAdmin: z.boolean(),
  isMarketingUser: z.boolean(),
  canImpersonate: z.boolean(),
  /** May start a demo session (admin or `demo_presenter`). Defaults to false so a response from a server that
   * predates the field still parses to a boolean. Gates the demo picker only. */
  canStartDemo: z.boolean().default(false),
  isManager: z.boolean(),
  impersonation: ImpersonationMetaSchema.nullable(),
  userId: z.number().nullable(),
  /** EFFECTIVE (team-lensed) userType: the user's real userType unless a
   * "UI View" team lens is set, then the userType that team carries.
   * Role-gated UI follows this. Never for analytics identity (use
   * `realUserType`) and never for permissions (RBAC lives in `roles`). */
  userType: z.string().nullable(),
  /** EFFECTIVE (team-lensed) Flow team derived from `userType`; role-gated UI
   * follows it. */
  flowRole: FlowRoleOrNull,
  /** Unlensed userType, for analytics identity only. */
  realUserType: z.string().nullable(),
  /** Unlensed Flow team, for analytics identity only. */
  realFlowRole: FlowRoleOrNull,
  /** True while a team lens is stored (the effective team differs from the
   * real one). */
  viewingAs: z.boolean(),
  email: z.string().nullable(),
  /** Signed-in ('auth'-scoped, not 'acting') user's granted role slugs — must
   * match isAdmin's scope so frontend role guards don't flip under
   * impersonation. */
  roles: z.array(z.string()),
  /** Trellus dial seats the signed-in person holds. Everyone can power dial; `parallel` is true only for an exact
   * `parallel_dialer` grant on the AUTH user (the real signed-in person dials, never the impersonated one). */
  dialSeats: z.object({ parallel: z.boolean() }),
});

export type UserControls = z.infer<typeof UserControlsSchema>;
